import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomersByName } from '@src/hooks/useCustomer';
import { Card, Flex, LoaderTalos, Table, Tbody, Td, Text, Th, Tr } from '@talos/kyoko';
import { useGetCustomerConfigurationQuery } from './customerConfigurationApi';

interface CustomerConfigurationCardProps {
  selectedCustomers: string[] | undefined;
  showCustomerTiering: boolean;
}

export function CustomerConfigurationCard({ selectedCustomers, showCustomerTiering }: CustomerConfigurationCardProps) {
  const customerName = selectedCustomers?.length === 1 ? selectedCustomers[0] : undefined;
  const query = useGetCustomerConfigurationQuery(customerName ?? skipToken);
  const customersByName = useCustomersByName();

  if (customerName == null) {
    return null;
  }

  const title = customersByName?.get(customerName)?.DisplayName || customerName;

  return (
    <Card title={title} data-testid="customer-configuration-card" mt="spacing16" p="spacing12">
      {query.isLoading ? (
        <Flex justifyContent="center">
          <LoaderTalos delay={300} />
        </Flex>
      ) : query.isError ? (
        <Text color="colorTextNegative">Unable to load customer configuration.</Text>
      ) : query.data ? (
        <Table w="100%">
          <Tbody>
            {showCustomerTiering && (
              <>
                <Tr>
                  <Th>Base Tier</Th>
                  <Td align="right" data-testid="customer-configuration-all-in-tier">
                    {query.data.AllInTier || '-'}
                  </Td>
                </Tr>
                <Tr>
                  <Th>Fee Tier</Th>
                  <Td align="right" data-testid="customer-configuration-fee-tier">
                    {query.data.FeeTier || '-'}
                  </Td>
                </Tr>
                <Tr>
                  <Th>Symbol Group</Th>
                  <Td align="right" data-testid="customer-configuration-symbol-group">
                    {query.data.SymbolGroup || '-'}
                  </Td>
                </Tr>
              </>
            )}
            <Tr>
              <Th>Mode</Th>
              <Td align="right" data-testid="customer-configuration-mode">
                {query.data.Mode}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      ) : query.isSuccess ? (
        <Text>No configuration found.</Text>
      ) : null}
    </Card>
  );
}
